import React, { useState, useEffect, forwardRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { API_BASE_URL, getAuthHeaders } from '../../config/api';
import { PlusIcon, PencilIcon, TrashIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

const UniversityFAQManagement = forwardRef((props, ref) => {
 const { token } = useAuth();
 const { showSuccess, showError } = useToast();
 const [faqs, setFaqs] = useState([]);
 const [loading, setLoading] = useState(true);
 const [adding, setAdding] = useState(false);
 const [editing, setEditing] = useState(null);
 const [newFaq, setNewFaq] = useState({ question: '', answer: '' });

 useEffect(() => {
 fetchFAQs();
 }, []);

 const fetchFAQs = async () => {
 try {
 const response = await fetch(`${API_BASE_URL}/university/faqs`, {
 headers: getAuthHeaders(token)
 });
 if (response.ok) {
 const data = await response.json();
 setFaqs(data.faqs || []);
 }
 } catch (error) {
 console.error('Error fetching FAQs:', error);
 } finally {
 setLoading(false);
 }
 };

 const handleAddFaq = async () => {
 if (!newFaq.question.trim() || !newFaq.answer.trim()) {
 showError('Question and answer are required');
 return;
 }

 setAdding(true);
 try {
 const response = await fetch(`${API_BASE_URL}/university/faqs`, {
 method: 'POST',
 headers: getAuthHeaders(token),
 body: JSON.stringify(newFaq)
 });

 if (response.ok) {
 const data = await response.json();
 setFaqs([...faqs, data.faq]);
 setNewFaq({ question: '', answer: '' });
 showSuccess('FAQ added successfully');
 } else {
 const error = await response.json();
 showError(error.error || 'Failed to add FAQ');
 }
 } catch (error) {
 showError('Failed to add FAQ');
 } finally {
 setAdding(false);
 }
 };

 const handleUpdateFaq = async (faqId, updatedData) => {
 try {
 const response = await fetch(`${API_BASE_URL}/university/faqs/${faqId}`, {
 method: 'PUT',
 headers: getAuthHeaders(token),
 body: JSON.stringify(updatedData)
 });

 if (response.ok) {
 const data = await response.json();
 setFaqs(faqs.map(f => f.id === faqId ? data.faq : f));
 setEditing(null);
 showSuccess('FAQ updated successfully');
 } else {
 const error = await response.json();
 showError(error.error || 'Failed to update FAQ');
 }
 } catch (error) {
 showError('Failed to update FAQ');
 }
 };

 const handleDeleteFaq = async (faqId) => {
 if (!window.confirm('Are you sure you want to delete this FAQ?')) return;

 try {
 const response = await fetch(`${API_BASE_URL}/university/faqs/${faqId}`, {
 method: 'DELETE',
 headers: getAuthHeaders(token)
 });

 if (response.ok) {
 setFaqs(faqs.filter(f => f.id !== faqId));
 showSuccess('FAQ deleted successfully');
 } else {
 const error = await response.json();
 showError(error.error || 'Failed to delete FAQ');
 }
 } catch (error) {
 showError('Failed to delete FAQ');
 }
 };

 if (loading) {
 return (
 <div className="card relative overflow-hidden">
 <div className="text-center py-8">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
 <p className="mt-4 text-slate-500 text-sm font-semibold">Loading FAQs...</p>
 </div>
 </div>
 );
 }

 return (
 <div ref={ref} className="card relative overflow-hidden">
 <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-3">
 <div>
 <h3 className="text-lg font-bold text-slate-800">Frequently Asked Questions</h3>
 <p className="text-sm text-slate-500 mt-1">Manage FAQs that students can see on your university profile</p>
 </div>
 </div>

 {/* Add New FAQ Form */}
 <div className="mb-6 p-5 bg-slate-50 border border-slate-200 rounded-2xl">
 <h4 className="text-sm font-bold text-slate-700 mb-3">Add New FAQ</h4>
 <div className="space-y-4">
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Question *</label>
 <input
 type="text"
 value={newFaq.question}
 onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
 placeholder="e.g., What are the admission requirements?"
 className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white text-slate-700 transition-all duration-300"
 />
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Answer *</label>
 <textarea
 value={newFaq.answer}
 onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
 placeholder="Provide a detailed answer..."
 rows="3"
 className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white text-slate-700 transition-all duration-300"
 />
 </div>
 <button
 onClick={handleAddFaq}
 disabled={adding}
 className="btn-primary w-full sm:w-auto flex items-center justify-center space-x-2 py-2 px-5 text-sm disabled:opacity-50"
 >
 <PlusIcon className="h-4 w-4" />
 <span>{adding ? 'Adding...' : 'Add FAQ'}</span>
 </button>
 </div>
 </div>

 {/* FAQs List */}
 {faqs.length > 0 ? (
 <div className="space-y-4">
 <h4 className="font-bold text-slate-700 text-sm mb-3">Current FAQs ({faqs.length})</h4>
 {faqs.map((faq) => (
 <div key={faq.id} className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-primary-100 hover:shadow-sm transition-all duration-300">
 {editing?.id === faq.id ? (
 <div className="space-y-4 animate-fade-in">
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Question</label>
 <input
 type="text"
 value={editing.question}
 onChange={(e) => setEditing({ ...editing, question: e.target.value })}
 className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white text-slate-700 transition-all duration-300"
 />
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Answer</label>
 <textarea
 value={editing.answer}
 onChange={(e) => setEditing({ ...editing, answer: e.target.value })}
 rows="3"
 className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white text-slate-700 transition-all duration-300"
 />
 </div>
 <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-slate-100">
 <button
 onClick={() => handleUpdateFaq(faq.id, editing)}
 className="btn-primary py-2 px-5 text-sm"
 >
 Save
 </button>
 <button
 onClick={() => setEditing(null)}
 className="btn-secondary py-2 px-5 text-sm"
 >
 Cancel
 </button>
 </div>
 </div>
 ) : (
 <>
 <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
 <div className="flex items-start space-x-3 flex-1 min-w-0">
 <div className="p-2.5 bg-primary-50 rounded-xl text-primary-600 flex-shrink-0">
 <QuestionMarkCircleIcon className="h-5 w-5" />
 </div>
 <div className="flex-1 min-w-0">
 <h5 className="font-bold text-slate-700 break-words leading-snug">{faq.question}</h5>
 <p className="text-sm text-slate-500 mt-2 break-words leading-relaxed">{faq.answer}</p>
 </div>
 </div>
 <div className="flex sm:flex-col gap-2 sm:ml-4 flex-shrink-0">
 <button
 onClick={() => setEditing(faq)}
 className="text-primary-600 hover:text-primary-700 p-2.5 rounded-xl hover:bg-primary-50 transition-all active:scale-95 border border-slate-100"
 title="Edit FAQ"
 >
 <PencilIcon className="h-4.5 w-4.5 mx-auto" />
 </button>
 <button
 onClick={() => handleDeleteFaq(faq.id)}
 className="text-red-500 hover:text-red-700 p-2.5 rounded-xl hover:bg-red-50 transition-all active:scale-95 border border-slate-100"
 title="Delete FAQ"
 >
 <TrashIcon className="h-4.5 w-4.5 mx-auto" />
 </button>
 </div>
 </div>
 </>
 )}
 </div>
 ))}
 </div>
 ) : (
 <div className="text-center py-12 text-slate-400 bg-slate-50 border border-slate-100 rounded-2xl">
 <QuestionMarkCircleIcon className="h-16 w-16 mx-auto mb-4 text-slate-300 animate-pulse" />
 <p className="font-bold text-slate-700 text-lg">No FAQs Added</p>
 <p className="text-sm mt-1 max-w-sm mx-auto">Add frequently asked questions to help students learn more about your university.</p>
 </div>
 )}
 </div>
 );
});

UniversityFAQManagement.displayName = 'UniversityFAQManagement';

export default UniversityFAQManagement;
